import { TERMINAL_STATUSES } from './types';
import type {
  DayStr,
  EpochMs,
  Message,
  Milestone,
  MilestoneState,
  MockCache,
  ProjectKey,
  SubscriptionTarget,
  Task,
  TaskId,
  TaskKey,
  TaskStatus,
  UserId,
  Watcher,
  WatchReason,
} from './types';

export function taskRef(projectKey: ProjectKey, number: number): TaskKey {
  return `${projectKey}-${number}` as TaskKey;
}

export function dmKey(userIds: UserId[]): string {
  return userIds
    .map((id) => String(id))
    .sort()
    .join(':');
}

export function normalizeEmoji(emoji: string): string {
  return emoji.replace(/[\u{1F3FB}-\u{1F3FF}]/gu, '');
}

export interface MessageBlock {
  authorId: UserId;
  dayKey: string;
  messages: Message[];
}

function dayKeyInZone(ms: number, timeZone: string): string {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  try {
    return new Intl.DateTimeFormat('en-CA', { ...options, timeZone }).format(ms);
  } catch {
    return new Intl.DateTimeFormat('en-CA', { ...options, timeZone: 'UTC' }).format(ms);
  }
}

export function groupMessages(messages: Message[], tz: string): MessageBlock[] {
  const blocks: MessageBlock[] = [];
  const GROUP_WINDOW_MS = 60 * 60 * 1000;
  for (const message of messages) {
    const dayKey = dayKeyInZone(Number(message.createdAt), tz);
    const current = blocks.length > 0 ? blocks[blocks.length - 1] : undefined;
    const previous = current !== undefined && current.messages.length > 0
      ? current.messages[current.messages.length - 1]
      : undefined;
    const continues =
      current !== undefined &&
      previous !== undefined &&
      message.systemKind === null &&
      previous.systemKind === null &&
      current.authorId === message.authorId &&
      current.dayKey === dayKey &&
      Number(message.createdAt) - Number(previous.createdAt) < GROUP_WINDOW_MS;
    if (continues && current !== undefined) {
      current.messages.push(message);
    } else {
      blocks.push({ authorId: message.authorId, dayKey, messages: [message] });
    }
  }
  return blocks;
}

export function rollupEstimateMinutes(
  own: number | null,
  children: (number | null)[],
): number | null {
  if (children.length === 0) {
    return own;
  }
  let sum = 0;
  let known = 0;
  for (const child of children) {
    if (child !== null) {
      sum += child;
      known += 1;
    }
  }
  return known > 0 ? sum : null;
}

export function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) {
    return `${minutes} мин`;
  }
  if (rest === 0) {
    return `${hours} ч`;
  }
  return `${hours} ч ${rest} мин`;
}

export function isSequenceBroken(
  pred: { startOn: DayStr | null; dueOn: DayStr | null },
  dep: { startOn: DayStr | null; dueOn: DayStr | null },
  timeZone: string,
): boolean {
  void timeZone;
  if (pred.dueOn === null || dep.startOn === null) {
    return false;
  }
  return pred.dueOn > dep.startOn;
}

export interface BlockCycleVerdict {
  hasCycle: boolean;
  path: TaskId[];
}

export function findBlockCycle(
  edges: Array<{ fromTaskId: TaskId; toTaskId: TaskId }>,
  from: TaskId,
  to: TaskId,
): BlockCycleVerdict {
  if (from === to) {
    return { hasCycle: true, path: [from, to] };
  }
  const outgoing = new Map<TaskId, TaskId[]>();
  for (const edge of edges) {
    const list = outgoing.get(edge.fromTaskId);
    if (list === undefined) {
      outgoing.set(edge.fromTaskId, [edge.toTaskId]);
    } else if (!list.includes(edge.toTaskId)) {
      list.push(edge.toTaskId);
    }
  }
  const visited = new Set<TaskId>([to]);
  const parent = new Map<TaskId, TaskId>();
  const stack: TaskId[] = [to];
  while (stack.length > 0) {
    const current = stack.pop() as TaskId;
    if (current === from) {
      const tail: TaskId[] = [from];
      let node: TaskId = from;
      while (node !== to) {
        const previous = parent.get(node) as TaskId;
        tail.unshift(previous);
        node = previous;
      }
      return { hasCycle: true, path: [from, ...tail] };
    }
    for (const next of outgoing.get(current) ?? []) {
      if (!visited.has(next)) {
        visited.add(next);
        parent.set(next, current);
        stack.push(next);
      }
    }
  }
  return { hasCycle: false, path: [] };
}

export function computeProgress(task: Task): { done: number; total: number } {
  const closed = (TERMINAL_STATUSES as readonly string[]).includes(task.status);
  return closed ? { done: 1, total: 1 } : { done: 0, total: 1 };
}

export function milestoneState(m: Milestone, nowMs: EpochMs): MilestoneState {
  if (m.completedAt !== null) {
    return 'reached';
  }
  if (m.dueOn === null) {
    return 'open';
  }
  const today = new Date(Number(nowMs)).toISOString().slice(0, 10);
  return m.dueOn < today ? 'overdue' : 'open';
}

const REASON_ORDER: WatchReason[] = ['creator', 'assignee', 'commenter', 'manual'];

export function watchersOf(cache: MockCache, target: SubscriptionTarget): Watcher[] {
  const reasons = new Map<UserId, Set<WatchReason>>();
  const explicit = new Set<UserId>();
  const muted = new Set<UserId>();

  const addReason = (userId: UserId, reason: WatchReason): void => {
    const set = reasons.get(userId);
    if (set === undefined) {
      reasons.set(userId, new Set([reason]));
    } else {
      set.add(reason);
    }
  };

  if (target.kind === 'task') {
    const task = cache.tasks.get(target.id);
    if (task !== undefined) {
      addReason(task.creatorId, 'creator');
      if (task.assigneeId !== null) {
        addReason(task.assigneeId, 'assignee');
      }
      for (const comment of cache.comments.values()) {
        if (comment.taskId === task.id && comment.deletedAt === null) {
          addReason(comment.authorId, 'commenter');
        }
      }
    }
  }

  for (const subscription of cache.subscriptions.values()) {
    if (
      subscription.targetKind === target.kind &&
      subscription.targetId === String(target.id)
    ) {
      explicit.add(subscription.userId);
      if (subscription.state === 'on') {
        addReason(subscription.userId, 'manual');
      } else {
        muted.add(subscription.userId);
        if (!reasons.has(subscription.userId)) {
          reasons.set(subscription.userId, new Set());
        }
      }
    }
  }

  const watchers: Watcher[] = [];
  for (const [userId, reasonSet] of reasons) {
    watchers.push({
      userId,
      reasons: REASON_ORDER.filter((reason) => reasonSet.has(reason)),
      explicit: explicit.has(userId),
      muted: muted.has(userId),
    });
  }
  watchers.sort((a, b) => (String(a.userId) < String(b.userId) ? -1 : 1));
  return watchers;
}

export function statusLabel(status: TaskStatus): string {
  switch (status) {
    case 'backlog':
      return 'Бэклог';
    case 'todo':
      return 'К выполнению';
    case 'in_progress':
      return 'В работе';
    case 'in_review':
      return 'На ревью';
    case 'done':
      return 'Готово';
    case 'canceled':
      return 'Отменено';
  }
}
